import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * MarkdownContent - Componente para renderizar markdown (compatible con contenido de Notion)
 * 
 * Soporta:
 * - **negrita** y __negrita__
 * - *cursiva* y _cursiva_
 * - ~~tachado~~
 * - [enlaces](url)
 * - Saltos de línea
 * - Listas con - o *
 * - Encabezados # ## ###
 * - {{video:YOUTUBE_URL}} - Videos embebidos de YouTube
 * - URLs de YouTube pegadas directamente en su propia línea también se incrustan automáticamente
 * 
 * Props:
 * - content: String con markdown
 * - className: Clases CSS adicionales
 */

// Extract YouTube video ID from various URL formats
const getYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const YouTubeEmbed = ({ url }) => {
  const videoId = getYouTubeId(url.trim());
  if (!videoId) return <p className="text-red-400 text-sm italic">Video no válido: {url}</p>;
  
  return (
    <div className="my-6" data-testid="youtube-embed">
      <div className="aspect-video rounded-xl overflow-hidden shadow-sm border border-gray-100">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Video"
          loading="lazy"
        />
      </div>
    </div>
  );
};

const markdownComponents = {
  p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--accent))] hover:underline">
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="mb-1">{children}</li>,
  h1: ({ children }) => <h1 className="text-2xl font-heading mb-4">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-heading mb-3">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-heading mb-2">{children}</h3>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-[hsl(var(--accent))] pl-4 italic text-[hsl(var(--text-secondary))] my-4">
      {children}
    </blockquote>
  ),
  code: ({ children }) => (
    <code className="bg-[hsl(var(--sage-light))] px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
  ),
  hr: () => <hr className="border-t border-[hsl(var(--border))] my-6" />,
  img: ({ src, alt }) => (
    <img
      src={src}
      alt={alt || ''}
      className="rounded-xl shadow-sm border border-gray-100 max-w-full h-auto my-4"
      loading="lazy"
    />
  ),
  table: ({ children }) => (
    <div className="my-6 overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[hsl(var(--sage-light))]">{children}</thead>
  ),
  tbody: ({ children }) => <tbody className="divide-y divide-gray-100">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-gray-50/50 transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-3 text-left font-semibold text-[hsl(var(--text-primary))] border-b border-gray-200">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 text-[hsl(var(--text-secondary))]">{children}</td>
  ),
  del: ({ children }) => <del className="line-through text-gray-400">{children}</del>,
};

const preprocessContent = (text) => {
  if (typeof text !== 'string') return '';
  let result = text
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n');
  
  // Fix collapsed Markdown tables (all on one line from Notion copy-paste)
  // Detects pattern: | text | text | | -- | -- | | data | data |
  // and restores newlines between rows
  result = result.replace(
    /^(.*?)(\|[^|\n]+(?:\|[^|\n]+)+\|\s*\|\s*[-:]+\s*(?:\|\s*[-:]+\s*)+\|.+)$/gm,
    (match, before, tableBlock) => {
      // Split on the pattern: | ... | followed by a new | that starts a row
      // A row boundary is where we see "| |" (end of row pipe, space, start of next row pipe)
      const restored = tableBlock.replace(/\s*\|\s*\|/g, '|\n|');
      return before + restored;
    }
  );
  
  return result;
};

// Preprocess: convert raw YouTube URLs on their own line into {{video:URL}} syntax
const convertRawYouTubeUrls = (text) => {
  if (!text || typeof text !== 'string') return text || '';
  try {
    // Match YouTube URLs that appear alone on a line (not already inside {{video:}})
    return text.replace(
      /^([ \t]*)((?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]+(?:[?&][\w=-]*)*)[ \t]*$/gm,
      (fullMatch, indent, url) => {
        return `${indent}{{video:${url.trim()}}}`;
      }
    );
  } catch {
    return text;
  }
};

const MarkdownContent = ({ content, className = "" }) => {
  if (!content) return null;

  // Step 1: Convert raw YouTube URLs to {{video:URL}} syntax
  const normalizedContent = convertRawYouTubeUrls(content);

  // Step 2: Split content into text blocks and video blocks
  // Pattern: {{video:URL}} on its own or inline
  const videoPattern = /\{\{video:(.*?)\}\}/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = videoPattern.exec(normalizedContent)) !== null) {
    // Add text before the video
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: normalizedContent.slice(lastIndex, match.index) });
    }
    // Add video
    parts.push({ type: 'video', url: match[1].trim() });
    lastIndex = match.index + match[0].length;
  }
  // Add remaining text
  if (lastIndex < normalizedContent.length) {
    parts.push({ type: 'text', content: normalizedContent.slice(lastIndex) });
  }

  // If no videos found, render as pure markdown (unchanged behavior)
  if (parts.length <= 1 && parts[0]?.type === 'text') {
    const processed = preprocessContent(normalizedContent);
    return (
      <div className={`markdown-content notranslate ${className}`} translate="no" lang="es">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {processed}
        </ReactMarkdown>
      </div>
    );
  }

  // Mixed content: text + videos
  return (
    <div className={`markdown-content notranslate ${className}`} translate="no" lang="es">
      {parts
        .filter(part => {
          if (part.type === 'text') {
            const processed = preprocessContent(part.content);
            return processed.trim().length > 0;
          }
          return true;
        })
        .map((part, i) => {
          if (part.type === 'video') {
            return <YouTubeEmbed key={i} url={part.url} />;
          }
          const processed = preprocessContent(part.content);
          return (
            <ReactMarkdown key={i} remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {processed}
            </ReactMarkdown>
          );
        })}
    </div>
  );
};

export default MarkdownContent;
